import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IQuestionTemplate from '../../interfaces/IQuestionTemplate'
import { SearchRequest, QuestionTemplateSaveRequest } from '../../services/api/interfaces/ApiRequest'
import { questionTemplateList, questionTemplateSave } from '../../services/api'

interface QuestionTemplateContextState extends BaseContextState {
  questionTemplates: Array<IQuestionTemplate> | null,
  questionTemplate: IQuestionTemplate | null
}

export interface QuestionTemplateContextData extends QuestionTemplateContextState {
  resetErrors: Function,
  requestQuestionTemplateList: (args: SearchRequest) => Promise<any>,
  requestQuestionTemplateFind: (args: SearchRequest) => void,
  requestQuestionTemplateSave: (args: QuestionTemplateSaveRequest) => void,
}

// Create context
const { Provider, Consumer } = createContext({} as QuestionTemplateContextData)

// Export consumer
export const QuestionTemplateConsumer = Consumer

export class QuestionTemplateProvider extends BaseContextProvider {
  state: QuestionTemplateContextState = {
    questionTemplates: null,
    questionTemplate: null
  }

  /**
   * Make api request for question template list
   * 
   * @param {SearchRequest} payload 
   */
  requestQuestionTemplateList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        questionTemplate: null
      }, async () => {
        const response = await questionTemplateList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'questionTemplates'
          },
        })

        if (this.state.success) {
          resolve(this.state.questionTemplates)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for question template finder
   * 
   * @param {SearchRequest} payload 
   */
  requestQuestionTemplateFind = (payload: SearchRequest) => {
    this.setState({
      fetching: true,
      error: null,
      questionTemplate: null
    }, async () => {
      payload.options = {
        ...payload.options,
        _single: true
      }

      const response = await questionTemplateList(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: {
          destination: 'questionTemplate'
        },
      })
    })
  }

  /**
   * Make api request for question template save
   * 
   * @param {QuestionTemplateSaveRequest} payload 
   */
  requestQuestionTemplateSave = (payload: QuestionTemplateSaveRequest) => {
    this.setState({
      fetching: true,
      error: null,
      questionTemplate: null
    }, async () => {
      const response = await questionTemplateSave(payload)

      // Process our response
      this.processResponse(response, { 
        mappings: [
          { origin: 'question_template', destination: 'questionTemplate' }
        ]
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestQuestionTemplateList: this.requestQuestionTemplateList,
          requestQuestionTemplateFind: this.requestQuestionTemplateFind,
          requestQuestionTemplateSave: this.requestQuestionTemplateSave,
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
